import React, { useState, useEffect } from 'react'
import Layout from '../../../../components/vision/Layout'
import ProtectedRoute from '../../../../components/vision/ProtectedRoute'

interface Indicador {
  id: string
  nome: string
  tipo: string
  categoria: string
}

interface Meta {
  id: string
  indicador_tipo_id: string
  valor_meta: number
  observacao: string
}

interface Resultado {
  indicador_tipo_id: string
  valor_realizado: number
  data_inicio: string
  data_fim: string
  mes_referencia: string
  data_lancamento: string
  observacao: string
}

export default function ResultadosPage() {
  const [clientes, setClientes] = useState<any[]>([])
  const [selectedCliente, setSelectedCliente] = useState<any>(null)
  const [indicadoresCliente, setIndicadoresCliente] = useState<Indicador[]>([])
  const [mesCompetencia, setMesCompetencia] = useState<string>('')
  const [resultados, setResultados] = useState<Resultado[]>([])
  const [resultadosSalvos, setResultadosSalvos] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [calculando, setCalculando] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [showLancamentoForm, setShowLancamentoForm] = useState(false)
  const [showClientesList, setShowClientesList] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [resultadoEmEdicao, setResultadoEmEdicao] = useState<any>(null)
  const [valorEdicao, setValorEdicao] = useState('')
  const [valorEdicaoFormatado, setValorEdicaoFormatado] = useState('')
  const [dataInicioEdicao, setDataInicioEdicao] = useState('')
  const [dataFimEdicao, setDataFimEdicao] = useState('')
  const [observacaoEdicao, setObservacaoEdicao] = useState('')
  const [modoLancamento, setModoLancamento] = useState<'mensal' | 'semanal'>('mensal')
  const [semanaAtual, setSemanaAtual] = useState<number>(1)
  const [expandedIndicadores, setExpandedIndicadores] = useState<Set<string>>(new Set())

  useEffect(() => {
    fetchClientes()
  }, [])

  useEffect(() => {
    if (selectedCliente && mesCompetencia && indicadoresCliente.length > 0) {
      fetchResultadosSalvos(selectedCliente.id, mesCompetencia)
    }
  }, [selectedCliente, mesCompetencia, indicadoresCliente])

  const fetchClientes = async () => {
    try {
      const res = await fetch('/api/gestao/clientes')
      if (res.ok) {
        const response = await res.json()
        const clientesData = response.data || response
        setClientes(Array.isArray(clientesData) ? clientesData : [])
      }
    } catch (error) {
      console.error('Erro ao buscar clientes:', error)
      setClientes([])
    }
  }

  const fetchIndicadoresCliente = async (clienteId: string) => {
    try {
      const res = await fetch(`/api/gestao/kpis/cliente-indicadores?cliente_id=${clienteId}`)
      if (res.ok) {
        const data = await res.json()
        if (Array.isArray(data)) {
          const indicadoresCompletos = await Promise.all(
            data.map(async (ci: any) => {
              const resInd = await fetch(`/api/gestao/kpis/indicador-tipos?id=${ci.indicador_tipo_id}`)
              if (resInd.ok) {
                const ind = await resInd.json()
                return ind
              }
              return null
            })
          )
          // Filtrar apenas indicadores de lançamento manual (excluir calculados)
          const indicadoresLancamento = indicadoresCompletos.filter(i => 
            i !== null && (i.tipo_indicador === 'lancamento' || !i.tipo_indicador)
          )
          setIndicadoresCliente(indicadoresLancamento)
        }
      }
    } catch (error) {
      console.error('Erro ao buscar indicadores do cliente:', error)
    }
  }

  const fetchResultadosSalvos = async (clienteId: string, mesCompetencia: string) => {
    try {
      const mesRef = mesCompetencia + '-01'
      const res = await fetch(`/api/gestao/kpis/resultados-mensais?mes_referencia=${mesRef}&cliente_id=${clienteId}`)
      if (res.ok) {
        const data = await res.json()
        setResultadosSalvos(data)
      } else {
        setResultadosSalvos([])
      }
    } catch (error) {
      console.error('Erro ao buscar resultados salvos:', error)
      setResultadosSalvos([])
    }
  }

  const handleSelectCliente = async (cliente: any) => {
    setSelectedCliente(cliente)
    setShowClientesList(false)
    setSearchTerm('')
    setLoading(true)
    setResultados([])
    setMesCompetencia('')
    setShowLancamentoForm(false)
    await fetchIndicadoresCliente(cliente.id)
    setLoading(false)
  }

  const calcularDatasIniciais = (mesCompetencia: string) => {
    const [ano, mes] = mesCompetencia.split('-')
    const primeiroDia = `${ano}-${mes}-01`
    
    // Data atual
    const hoje = new Date()
    const anoAtual = hoje.getFullYear()
    const mesAtual = hoje.getMonth() + 1
    
    // Verifica se é o mês atual
    const ehMesAtual = parseInt(ano) === anoAtual && parseInt(mes) === mesAtual
    
    let ultimoDia: string
    
    if (ehMesAtual) {
      // Mês atual: até hoje
      ultimoDia = hoje.toISOString().split('T')[0]
    } else {
      // Mês passado ou futuro: último dia do mês
      const ultimoDiaDoMes = new Date(parseInt(ano), parseInt(mes), 0).getDate()
      ultimoDia = `${ano}-${mes}-${String(ultimoDiaDoMes).padStart(2, '0')}`
    }
    
    return { data_inicio: primeiroDia, data_fim: ultimoDia }
  }

  const calcularDatasSemanais = (mesCompetencia: string, numeroSemana: number) => {
    const [ano, mes] = mesCompetencia.split('-')
    const primeiroDiaDoMes = new Date(parseInt(ano), parseInt(mes) - 1, 1)
    
    // Calcular início da semana (offset de 7 dias por semana)
    const diasOffset = (numeroSemana - 1) * 7
    const dataInicio = new Date(primeiroDiaDoMes)
    dataInicio.setDate(dataInicio.getDate() + diasOffset)
    
    // Calcular fim da semana (6 dias após o início)
    const dataFim = new Date(dataInicio)
    dataFim.setDate(dataFim.getDate() + 6)
    
    // Garantir que não ultrapasse o último dia do mês
    const ultimoDiaDoMes = new Date(parseInt(ano), parseInt(mes), 0)
    if (dataFim > ultimoDiaDoMes) {
      dataFim.setTime(ultimoDiaDoMes.getTime())
    }
    
    // Garantir que não comece antes do primeiro dia do mês
    if (dataInicio < primeiroDiaDoMes) {
      dataInicio.setTime(primeiroDiaDoMes.getTime())
    }
    
    return {
      data_inicio: dataInicio.toISOString().split('T')[0],
      data_fim: dataFim.toISOString().split('T')[0]
    }
  }

  const calcularNumeroSemanas = (mesCompetencia: string) => {
    const [ano, mes] = mesCompetencia.split('-')
    const ultimoDiaDoMes = new Date(parseInt(ano), parseInt(mes), 0).getDate()
    return Math.ceil(ultimoDiaDoMes / 7)
  }

  const handleIniciarLancamento = () => {
    if (!mesCompetencia) {
      alert('⚠️ Selecione o mês de competência')
      return
    }
    
    const hoje = new Date().toISOString().split('T')[0]
    // Usar datas conforme modo selecionado
    const { data_inicio, data_fim } = modoLancamento === 'semanal' 
      ? calcularDatasSemanais(mesCompetencia, semanaAtual)
      : calcularDatasIniciais(mesCompetencia)
    
    // Se não há indicadores configurados, permitir lançamento livre
    if (indicadoresCliente.length === 0) {
      setShowLancamentoForm(true)
      return
    }
    
    // Filtrar apenas indicadores que ainda NÃO foram lançados (considerar apenas a semana atual no modo semanal)
    const resultadosDaPeriodo = modoLancamento === 'semanal'
      ? resultadosSalvos.filter(r => r.data_inicio >= data_inicio && r.data_fim <= data_fim)
      : resultadosSalvos
    const indicadoresJaLancados = resultadosDaPeriodo.map(r => r.indicador_tipo_id)
    const indicadoresBase = indicadoresCliente.filter(ind => ind.categoria !== 'calculado')
    const indicadoresFaltantes = indicadoresBase.filter(ind => !indicadoresJaLancados.includes(ind.id))
    
    if (indicadoresFaltantes.length === 0 && indicadoresBase.length > 0) {
      alert('✅ Todos os indicadores já foram lançados para este mês!')
      return
    }
    
    const resultadosIniciais = indicadoresFaltantes.map(ind => ({
      indicador_tipo_id: ind.id,
      valor_realizado: 0,
      data_inicio: data_inicio,
      data_fim: data_fim,
      mes_referencia: mesCompetencia + '-01',
      data_lancamento: hoje,
      observacao: ''
    }))
    
    setResultados(resultadosIniciais)
    setShowLancamentoForm(true)
  }

  const handleUpdateResultado = (indicadorId: string, campo: string, valor: any) => {
    const index = resultados.findIndex(r => r.indicador_tipo_id === indicadorId)
    if (index >= 0) {
      const novosResultados = [...resultados]
      novosResultados[index] = { ...novosResultados[index], [campo]: valor }
      setResultados(novosResultados)
    } else {
      const hoje = new Date().toISOString().split('T')[0]
      const mesRef = new Date().toISOString().substring(0, 8) + '01'
      setResultados([...resultados, {
        indicador_tipo_id: indicadorId,
        valor_realizado: campo === 'valor_realizado' ? valor : 0,
        data_inicio: campo === 'data_inicio' ? valor : hoje,
        data_fim: campo === 'data_fim' ? valor : hoje,
        mes_referencia: campo === 'mes_referencia' ? valor : mesRef,
        data_lancamento: campo === 'data_lancamento' ? valor : hoje,
        observacao: campo === 'observacao' ? valor : ''
      }])
    }
  }

  const validarDataNoMes = (data: string, mesCompetencia: string) => {
    const [ano, mes] = mesCompetencia.split('-')
    const dataObj = new Date(data + 'T00:00:00')
    const anoData = dataObj.getFullYear()
    const mesData = dataObj.getMonth() + 1
    
    return anoData === parseInt(ano) && mesData === parseInt(mes)
  }

  const handleSalvarResultados = async () => {
    // Validar datas antes de salvar
    for (const resultado of resultados) {
      if (resultado.valor_realizado > 0) {
        if (!validarDataNoMes(resultado.data_inicio, mesCompetencia)) {
          alert(`❌ ${getIndicadorNome(resultado.indicador_tipo_id)}: Data de início fora do mês de competência`)
          return
        }
        if (!validarDataNoMes(resultado.data_fim, mesCompetencia)) {
          alert(`❌ ${getIndicadorNome(resultado.indicador_tipo_id)}: Data de fim fora do mês de competência`)
          return
        }
        if (resultado.data_inicio > resultado.data_fim) {
          alert(`❌ ${getIndicadorNome(resultado.indicador_tipo_id)}: Data de início não pode ser maior que data de fim`)
          return
        }
      }
    }

    setSaving(true)
    try {
      // Buscar ou criar planejamento antes de salvar resultados
      const planejamentoId = await buscarOuCriarPlanejamento(selectedCliente.id, mesCompetencia)
      
      if (!planejamentoId) {
        throw new Error('Não foi possível obter planejamento')
      }
      
      const promises = resultados
        .filter(r => r.valor_realizado > 0)
        .map(async (resultado) => {
          return fetch('/api/gestao/kpis/resultados-mensais', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              planejamento_id: planejamentoId, // ✅ CORREÇÃO: Sempre incluir planejamento_id
              indicador_tipo_id: resultado.indicador_tipo_id,
              valor_realizado: resultado.valor_realizado,
              data_inicio: resultado.data_inicio,
              data_fim: resultado.data_fim,
              mes_referencia: mesCompetencia + '-01',
              data_lancamento: new Date().toISOString().split('T')[0],
              observacao: resultado.observacao
            })
          })
        })

      await Promise.all(promises)
      alert('✅ Resultados salvos com sucesso!')
      setResultados([])
      setShowLancamentoForm(false)
      // Recarregar lista de resultados salvos
      if (selectedCliente && mesCompetencia) {
        await fetchResultadosSalvos(selectedCliente.id, mesCompetencia)
      }
    } catch (error) {
      console.error('Erro ao salvar resultados:', error)
      alert('❌ Erro ao salvar resultados: ' + (error instanceof Error ? error.message : 'Erro desconhecido'))
    } finally {
      setSaving(false)
    }
  }

  const handleSalvarECalcular = async () => {
    // Validar datas antes de salvar
    for (const resultado of resultados) {
      if (resultado.valor_realizado > 0) {
        if (!validarDataNoMes(resultado.data_inicio, mesCompetencia)) {
          alert(`❌ ${getIndicadorNome(resultado.indicador_tipo_id)}: Data de início fora do mês de competência`)
          return
        }
        if (!validarDataNoMes(resultado.data_fim, mesCompetencia)) {
          alert(`❌ ${getIndicadorNome(resultado.indicador_tipo_id)}: Data de fim fora do mês de competência`)
          return
        }
        if (resultado.data_inicio > resultado.data_fim) {
          alert(`❌ ${getIndicadorNome(resultado.indicador_tipo_id)}: Data de início não pode ser maior que data de fim`)
          return
        }
      }
    }

    setSaving(true)
    try {
      // 1. Buscar ou criar planejamento antes de salvar resultados
      const planejamentoId = await buscarOuCriarPlanejamento(selectedCliente.id, mesCompetencia)
      
      if (!planejamentoId) {
        throw new Error('Não foi possível obter planejamento')
      }
      
      // 2. Salvar resultados
      const promises = resultados
        .filter(r => r.valor_realizado > 0)
        .map(async (resultado) => {
          return fetch('/api/gestao/kpis/resultados-mensais', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              planejamento_id: planejamentoId,
              indicador_tipo_id: resultado.indicador_tipo_id,
              valor_realizado: resultado.valor_realizado,
              data_inicio: resultado.data_inicio,
              data_fim: resultado.data_fim,
              mes_referencia: mesCompetencia + '-01',
              data_lancamento: new Date().toISOString().split('T')[0],
              observacao: resultado.observacao
            })
          })
        })

      await Promise.all(promises)
      
      // 3. Calcular indicadores
      setCalculando(true)
      const resCalculo = await fetch('/api/gestao/kpis/calcular-indicadores', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ planejamento_id: planejamentoId })
      })
      
      if (resCalculo.ok) {
        const resultado = await resCalculo.json()
        alert(`✅ Resultados salvos e ${resultado.calculados || 0} indicadores calculados com sucesso!`)
      } else {
        alert('✅ Resultados salvos, mas houve erro ao calcular indicadores')
      }
      
      setResultados([])
      setShowLancamentoForm(false)
      // Recarregar lista de resultados salvos
      if (selectedCliente && mesCompetencia) {
        await fetchResultadosSalvos(selectedCliente.id, mesCompetencia)
      }
    } catch (error) {
      console.error('Erro ao salvar e calcular:', error)
      alert('❌ Erro: ' + (error instanceof Error ? error.message : 'Erro desconhecido'))
    } finally {
      setSaving(false)
      setCalculando(false)
    }
  }

  // Buscar ou criar planejamento para o mês de competência
  const buscarOuCriarPlanejamento = async (clienteId: string, mesCompetencia: string) => {
    try {
      const mesRef = mesCompetencia + '-01'
      
      // Buscar planejamento existente
      const resPlano = await fetch(`/api/gestao/kpis/planejamento?cliente_id=${clienteId}&mes_competencia=${mesRef}`)
      if (resPlano.ok) {
        const planosData = await resPlano.json()
        // Garantir que seja array (compatibilidade com API antiga e nova)
        const planosArray = Array.isArray(planosData) ? planosData : (planosData ? [planosData] : [])
        if (planosArray.length > 0) {
          return planosArray[0].id
        }
      }
      
      // Se não existe, criar novo planejamento
      const resCreate = await fetch('/api/gestao/kpis/planejamento', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cliente_id: clienteId,
          mes_competencia: mesRef,
          status: 'planejado'
        })
      })
      
      if (resCreate.ok) {
        const novoPlano = await resCreate.json()
        return novoPlano.id
      }
      
      throw new Error('Não foi possível criar planejamento')
    } catch (error) {
      console.error('Erro ao buscar/criar planejamento:', error)
      throw error
    }
  }

  // Abrir modal de edição
  const handleEditarResultado = (resultado: any) => {
    setResultadoEmEdicao(resultado)
    const tipo = getIndicadorTipo(resultado.indicador_tipo_id)
    if (tipo === 'monetario') {
      setValorEdicao(resultado.valor_realizado.toString())
      setValorEdicaoFormatado(formatarValorMonetario(resultado.valor_realizado))
    } else {
      setValorEdicao(resultado.valor_realizado.toString())
      setValorEdicaoFormatado('')
    }
    setDataInicioEdicao(resultado.data_inicio)
    setDataFimEdicao(resultado.data_fim)
    setObservacaoEdicao(resultado.observacao || '')
    setShowEditModal(true)
  }

  // Cancelar edição
  const handleCancelarEdicao = () => {
    setShowEditModal(false)
    setResultadoEmEdicao(null)
    setValorEdicao('')
    setValorEdicaoFormatado('')
    setDataInicioEdicao('')
    setDataFimEdicao('')
    setObservacaoEdicao('')
  }

  // Salvar edição
  const handleSalvarEdicao = async () => {
    if (!resultadoEmEdicao) return

    // Validações
    if (!valorEdicao || parseFloat(valorEdicao) <= 0) {
      alert('❌ Valor deve ser maior que zero')
      return
    }

    if (dataInicioEdicao && dataFimEdicao && dataInicioEdicao > dataFimEdicao) {
      alert('❌ Data de início não pode ser maior que data de fim')
      return
    }

    setSaving(true)
    try {
      const res = await fetch('/api/gestao/kpis/resultados-mensais', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: resultadoEmEdicao.id,
          valor_realizado: parseFloat(valorEdicao),
          data_inicio: dataInicioEdicao,
          data_fim: dataFimEdicao,
          observacao: observacaoEdicao
        })
      })

      if (res.ok) {
        alert('✅ Resultado atualizado com sucesso!')
        handleCancelarEdicao()
        await fetchResultadosSalvos(selectedCliente.id, mesCompetencia)
      } else {
        const error = await res.json()
        alert('❌ Erro ao atualizar: ' + (error.error || 'Erro desconhecido'))
      }
    } catch (error) {
      console.error('Erro ao atualizar resultado:', error)
      alert('❌ Erro ao atualizar resultado')
    } finally {
      setSaving(false)
    }
  }

  // Excluir resultado
  const handleExcluirResultado = async (resultado: any) => {
    const indicadorNome = getIndicadorNome(resultado.indicador_tipo_id)
    
    if (!confirm(`Tem certeza que deseja excluir o resultado de "${indicadorNome}"?\n\nEsta ação não pode ser desfeita.`)) {
      return
    }

    setSaving(true)
    try {
      const res = await fetch(`/api/gestao/kpis/resultados-mensais?id=${resultado.id}`, {
        method: 'DELETE'
      })

      if (res.ok) {
        alert('✅ Resultado excluído com sucesso!')
        await fetchResultadosSalvos(selectedCliente.id, mesCompetencia)
      } else {
        const error = await res.json()
        alert('❌ Erro ao excluir: ' + (error.error || 'Erro desconhecido'))
      }
    } catch (error) {
      console.error('Erro ao excluir resultado:', error)
      alert('❌ Erro ao excluir resultado')
    } finally {
      setSaving(false)
    }
  }

  const getResultadoValor = (indicadorId: string) => {
    const resultado = resultados.find(r => r.indicador_tipo_id === indicadorId)
    return resultado?.valor_realizado || ''
  }

  const getResultadoData = (indicadorId: string) => {
    const resultado = resultados.find(r => r.indicador_tipo_id === indicadorId)
    return resultado?.data_lancamento || new Date().toISOString().split('T')[0]
  }

  const getResultadoDataInicio = (indicadorId: string) => {
    const resultado = resultados.find(r => r.indicador_tipo_id === indicadorId)
    return resultado?.data_inicio || new Date().toISOString().split('T')[0]
  }

  const getResultadoDataFim = (indicadorId: string) => {
    const resultado = resultados.find(r => r.indicador_tipo_id === indicadorId)
    return resultado?.data_fim || new Date().toISOString().split('T')[0]
  }

  const getResultadoMesReferencia = (indicadorId: string) => {
    const resultado = resultados.find(r => r.indicador_tipo_id === indicadorId)
    return resultado?.mes_referencia || new Date().toISOString().substring(0, 8) + '01'
  }

  const gerarOpcoesMeses = () => {
    const meses = []
    const hoje = new Date()
    
    for (let i = -12; i <= 3; i++) {
      const data = new Date(hoje.getFullYear(), hoje.getMonth() + i, 1)
      const valor = data.toISOString().substring(0, 8) + '01'
      const label = data.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
      meses.push({ value: valor, label: label.charAt(0).toUpperCase() + label.slice(1) })
    }
    
    return meses
  }

  const formatarValorMonetario = (valor: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(valor)
  }

  const formatarMoedaEmTempoReal = (valorCentavos: number) => {
    // Converte centavos para reais (ex: 1234 → 12.34)
    const valorReais = valorCentavos / 100
    return formatarValorMonetario(valorReais)
  }

  const handleValorMonetarioChange = (indicadorId: string, novoValor: string) => {
    // Remove tudo exceto dígitos
    const apenasNumeros = novoValor.replace(/\D/g, '')
    
    // Converte para número (em centavos)
    const valorEmCentavos = parseInt(apenasNumeros) || 0
    
    // Converte centavos para reais e salva
    const valorEmReais = valorEmCentavos / 100
    handleUpdateResultado(indicadorId, 'valor_realizado', valorEmReais)
  }

  const handleValorChange = (indicadorId: string, valor: string) => {
    const tipo = getIndicadorTipo(indicadorId)
    
    if (tipo === 'quantidade') {
      // Apenas números inteiros
      const valorInteiro = parseInt(valor) || 0
      handleUpdateResultado(indicadorId, 'valor_realizado', valorInteiro)
    } else if (tipo === 'monetario') {
      // Formatação em tempo real
      handleValorMonetarioChange(indicadorId, valor)
    } else {
      // Outros tipos (percentual, taxa) permitem decimais
      handleUpdateResultado(indicadorId, 'valor_realizado', parseFloat(valor) || 0)
    }
  }

  const getValorFormatado = (indicadorId: string) => {
    const valor = getResultadoValor(indicadorId)
    const tipo = getIndicadorTipo(indicadorId)
    
    if (!valor) return ''
    
    if (tipo === 'monetario') {
      const valorNumerico = parseFloat(valor.toString())
      return formatarValorMonetario(valorNumerico)
    }
    
    return valor.toString()
  }

  const getResultadoObs = (indicadorId: string) => {
    const resultado = resultados.find(r => r.indicador_tipo_id === indicadorId)
    return resultado?.observacao || ''
  }

  const getIndicadorNome = (indicadorId: string) => {
    return indicadoresCliente.find(i => i.id === indicadorId)?.nome || 'Indicador desconhecido'
  }

  const getIndicadorTipo = (indicadorId: string) => {
    return indicadoresCliente.find(i => i.id === indicadorId)?.tipo || ''
  }

  const filteredClientes = clientes.filter((cliente: any) => {
    const searchLower = searchTerm.toLowerCase()
    return (
      cliente.nome_empresa?.toLowerCase().includes(searchLower) ||
      cliente.nome_completo?.toLowerCase().includes(searchLower) ||
      cliente.email?.toLowerCase().includes(searchLower)
    )
  })

  const indicadoresParaMostrar = indicadoresCliente
    .filter(i => i.categoria !== 'calculado')
    .map(i => i.id)

  const resultadosFiltrados = (() => {
    if (modoLancamento !== 'semanal' || !mesCompetencia) return resultadosSalvos
    const { data_inicio: semanaInicio, data_fim: semanaFim } = calcularDatasSemanais(mesCompetencia, semanaAtual)
    return resultadosSalvos.filter(r => r.data_inicio >= semanaInicio && r.data_fim <= semanaFim)
  })()

  const toggleExpandIndicador = (indicadorId: string) => {
    setExpandedIndicadores(prev => {
      const next = new Set(prev)
      if (next.has(indicadorId)) next.delete(indicadorId)
      else next.add(indicadorId)
      return next
    })
  }

  // Agrupamento para o modo Mensal: um registro por indicador com a soma de todos os lançamentos
  const resultadosAgrupados = (() => {
    if (modoLancamento === 'semanal') return []
    const mapa: Record<string, {
      indicador_tipo_id: string
      valor_realizado: number
      data_inicio: string
      data_fim: string
      data_lancamento: string
      lancamentos: any[]
    }> = {}
    resultadosSalvos.forEach((r: any) => {
      const key = r.indicador_tipo_id
      if (!mapa[key]) {
        mapa[key] = {
          indicador_tipo_id: key,
          valor_realizado: 0,
          data_inicio: r.data_inicio,
          data_fim: r.data_fim,
          data_lancamento: r.data_lancamento,
          lancamentos: []
        }
      }
      mapa[key].valor_realizado += parseFloat(r.valor_realizado) || 0
      if (r.data_inicio < mapa[key].data_inicio) mapa[key].data_inicio = r.data_inicio
      if (r.data_fim > mapa[key].data_fim) mapa[key].data_fim = r.data_fim
      mapa[key].lancamentos.push(r)
    })
    return Object.values(mapa)
  })()

  return (
    <ProtectedRoute>
      <Layout>
        <div className="p-8 bg-gradient-to-br from-gray-50 to-blue-50 min-h-screen">
          <div className="mb-6">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
              Lançamento de Resultados
            </h1>
            <p className="text-gray-600">Lance os valores reais alcançados pelos indicadores</p>
          </div>

          {/* Seção 1: Busca de Clientes (Horizontal) */}
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <h2 className="text-xl font-bold text-gray-800">Selecione o Cliente</h2>
              <span className="text-sm text-gray-500">({clientes.length} cadastrados)</span>
            </div>
            
            {!selectedCliente ? (
              <div className="relative">
                <input
                  type="text"
                  placeholder="Buscar cliente..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => setShowClientesList(true)}
                  className="w-full pl-10 pr-10 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                />
                <svg className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}

                {showClientesList && (
                  <div className="absolute z-10 w-full mt-2 bg-white rounded-xl shadow-2xl border-2 border-gray-200 max-h-96 overflow-y-auto">
                    {filteredClientes.length > 0 ? (
                      <div className="py-2">
                        {filteredClientes.map((cliente: any) => (
                          <button
                            key={cliente.id}
                            onClick={() => handleSelectCliente(cliente)}
                            className="w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-semibold text-gray-800">
                              {cliente.nome_empresa || cliente.nome_completo}
                            </div>
                            {cliente.email && (
                              <div className="text-xs text-gray-500 mt-1">
                                {cliente.email}
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="px-4 py-8 text-center text-gray-500">
                        Nenhum cliente encontrado
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-4 rounded-xl shadow-lg">
                <div className="flex-1">
                  <div className="font-semibold text-lg">
                    {selectedCliente.nome_empresa || selectedCliente.nome_completo}
                  </div>
                  {selectedCliente.email && (
                    <div className="text-sm text-blue-100 mt-1">
                      {selectedCliente.email}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => {
                    setSelectedCliente(null)
                    setShowLancamentoForm(false)
                    setResultados([])
                    setMesCompetencia('')
                    setSearchTerm('')
                  }}
                  className="px-4 py-2 bg-white text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-all flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Trocar Cliente
                </button>
              </div>
            )}
          </div>

          {/* Seção 2: Mês de Competência */}
          {selectedCliente && !showLancamentoForm && (
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
              <div className="bg-gradient-to-r from-orange-500 to-red-500 px-6 py-5">
                <h2 className="text-xl font-bold text-white">
                  {selectedCliente.nome_empresa || selectedCliente.nome_completo}
                </h2>
                <p className="text-orange-100 text-sm mt-1">Selecione o mês de competência para lançar os resultados</p>
              </div>
              
              <div className="p-6">
                <div className="max-w-md">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Mês de Competência *
                  </label>
                  <input
                    type="month"
                    value={mesCompetencia}
                    onChange={(e) => setMesCompetencia(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none transition-all"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    ⚠️ As datas de lançamento devem estar dentro do mês selecionado
                  </p>
                </div>

                {/* Toggle Modo de Lançamento */}
                {mesCompetencia && (
                  <div className="mt-6 space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Modo de Lançamento
                      </label>
                      <div className="flex gap-3">
                        <button
                          onClick={() => {
                            setModoLancamento('mensal')
                            setSemanaAtual(1)
                          }}
                          className={`flex-1 px-4 py-3 rounded-xl font-semibold transition-all ${
                            modoLancamento === 'mensal'
                              ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          <div className="flex items-center justify-center gap-2">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            Mensal
                          </div>
                        </button>
                        <button
                          onClick={() => setModoLancamento('semanal')}
                          className={`flex-1 px-4 py-3 rounded-xl font-semibold transition-all ${
                            modoLancamento === 'semanal'
                              ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          <div className="flex items-center justify-center gap-2">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                            Semanal
                          </div>
                        </button>
                      </div>
                    </div>

                    {/* Seletor de Semana */}
                    {modoLancamento === 'semanal' && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                          Selecione a Semana
                        </label>
                        <div className="grid grid-cols-4 gap-3">
                          {Array.from({ length: calcularNumeroSemanas(mesCompetencia) }, (_, i) => i + 1).map((semana) => {
                            const { data_inicio, data_fim } = calcularDatasSemanais(mesCompetencia, semana)
                            return (
                              <button
                                key={semana}
                                onClick={() => setSemanaAtual(semana)}
                                className={`px-4 py-3 rounded-xl font-semibold transition-all ${
                                  semanaAtual === semana
                                    ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white shadow-lg'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                              >
                                <div className="text-sm">Semana {semana}</div>
                                <div className="text-xs mt-1 opacity-90">
                                  {new Date(data_inicio + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                                  {' - '}
                                  {new Date(data_fim + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                                </div>
                              </button>
                            )
                          })}
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          💡 Você pode lançar resultados para cada semana separadamente
                        </p>
                      </div>
                    )}
                  </div>
                )}
                
              </div>

              {/* Resultados Salvos */}
              {mesCompetencia && (modoLancamento === 'mensal' ? resultadosAgrupados.length > 0 : resultadosFiltrados.length > 0) && (
                <div className="p-6 border-t">
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                    </svg>
                    Resultados Lançados ({modoLancamento === 'mensal' ? resultadosAgrupados.length : resultadosFiltrados.length})
                    {modoLancamento === 'mensal' && resultadosSalvos.length > resultadosAgrupados.length && (
                      <span className="ml-2 text-xs font-normal text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full border border-blue-200">
                        {resultadosSalvos.length} lançamentos semanais agrupados
                      </span>
                    )}
                  </h3>
                  <div className="space-y-3">
                    {modoLancamento === 'mensal' ? (
                      // Modo Mensal: um grupo por indicador com a soma de todos os lançamentos
                      resultadosAgrupados.map((grupo: any) => (
                        <div key={grupo.indicador_tipo_id} className="bg-gray-50 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors overflow-hidden">
                          {/* Linha de resumo */}
                          <div className="p-4">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="font-semibold text-gray-800 mb-1">
                                  {getIndicadorNome(grupo.indicador_tipo_id)}
                                </div>
                                <div className="grid grid-cols-3 gap-4 text-sm">
                                  <div>
                                    <span className="text-gray-500">Total:</span>
                                    <span className="ml-2 font-semibold text-green-600">
                                      {getIndicadorTipo(grupo.indicador_tipo_id) === 'monetario'
                                        ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(grupo.valor_realizado)
                                        : grupo.valor_realizado}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">Período:</span>
                                    <span className="ml-2 font-medium text-gray-700">
                                      {new Date(grupo.data_inicio + 'T00:00:00').toLocaleDateString('pt-BR')} a {new Date(grupo.data_fim + 'T00:00:00').toLocaleDateString('pt-BR')}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-xs font-semibold px-2.5 py-1 rounded-full">
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                                      </svg>
                                      {grupo.lancamentos.length} {grupo.lancamentos.length === 1 ? 'lançamento' : 'lançamentos'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              {/* Botão expandir/recolher */}
                              <button
                                onClick={() => toggleExpandIndicador(grupo.indicador_tipo_id)}
                                className="ml-4 p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center gap-1 text-xs font-medium"
                                title={expandedIndicadores.has(grupo.indicador_tipo_id) ? 'Ocultar lançamentos' : 'Ver lançamentos individuais'}
                              >
                                <svg
                                  className={`w-4 h-4 transition-transform duration-200 ${expandedIndicadores.has(grupo.indicador_tipo_id) ? 'rotate-180' : ''}`}
                                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                                {expandedIndicadores.has(grupo.indicador_tipo_id) ? 'Ocultar' : 'Detalhes'}
                              </button>
                            </div>
                          </div>
                          {/* Lançamentos individuais (expandido) */}
                          {expandedIndicadores.has(grupo.indicador_tipo_id) && (
                            <div className="border-t border-blue-100 bg-blue-50/40 px-4 py-3 space-y-2">
                              {grupo.lancamentos.map((resultado: any) => (
                                <div key={resultado.id} className="bg-white rounded-lg p-3 border border-blue-100 flex justify-between items-start">
                                  <div className="flex-1">
                                    <div className="grid grid-cols-3 gap-4 text-sm">
                                      <div>
                                        <span className="text-gray-500">Valor:</span>
                                        <span className="ml-2 font-semibold text-green-600">
                                          {getIndicadorTipo(resultado.indicador_tipo_id) === 'monetario'
                                            ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(resultado.valor_realizado)
                                            : resultado.valor_realizado}
                                        </span>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">Período:</span>
                                        <span className="ml-2 font-medium text-gray-700">
                                          {new Date(resultado.data_inicio + 'T00:00:00').toLocaleDateString('pt-BR')} a {new Date(resultado.data_fim + 'T00:00:00').toLocaleDateString('pt-BR')}
                                        </span>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">Lançado em:</span>
                                        <span className="ml-2 font-medium text-gray-700">
                                          {new Date(resultado.data_lancamento + 'T00:00:00').toLocaleDateString('pt-BR')}
                                        </span>
                                      </div>
                                    </div>
                                    {resultado.observacao && (
                                      <div className="mt-1 text-sm text-gray-600">
                                        <span className="text-gray-500">Obs:</span> {resultado.observacao}
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex gap-2 ml-4">
                                    <button
                                      onClick={() => handleEditarResultado(resultado)}
                                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                      title="Editar resultado"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                      </svg>
                                    </button>
                                    <button
                                      onClick={() => handleExcluirResultado(resultado)}
                                      className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                      title="Excluir resultado"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      // Modo Semanal: registros individuais da semana selecionada (comportamento original)
                      resultadosFiltrados.map((resultado: any) => (
                        <div key={resultado.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-blue-300 transition-colors">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="font-semibold text-gray-800 mb-1">
                                {getIndicadorNome(resultado.indicador_tipo_id)}
                              </div>
                              <div className="grid grid-cols-3 gap-4 text-sm">
                                <div>
                                  <span className="text-gray-500">Valor:</span>
                                  <span className="ml-2 font-semibold text-green-600">
                                    {getIndicadorTipo(resultado.indicador_tipo_id) === 'monetario'
                                      ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(resultado.valor_realizado)
                                      : resultado.valor_realizado}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-gray-500">Período:</span>
                                  <span className="ml-2 font-medium text-gray-700">
                                    {new Date(resultado.data_inicio + 'T00:00:00').toLocaleDateString('pt-BR')} a {new Date(resultado.data_fim + 'T00:00:00').toLocaleDateString('pt-BR')}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-gray-500">Lançado em:</span>
                                  <span className="ml-2 font-medium text-gray-700">
                                    {new Date(resultado.data_lancamento + 'T00:00:00').toLocaleDateString('pt-BR')}
                                  </span>
                                </div>
                              </div>
                              {resultado.observacao && (
                                <div className="mt-2 text-sm text-gray-600">
                                  <span className="text-gray-500">Obs:</span> {resultado.observacao}
                                </div>
                              )}
                            </div>
                            {/* Botões de Ação */}
                            <div className="flex gap-2 ml-4">
                              <button
                                onClick={() => handleEditarResultado(resultado)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors group"
                                title="Editar resultado"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleExcluirResultado(resultado)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors group"
                                title="Excluir resultado"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Botão para adicionar mais resultados */}
                  <div className="mt-6 pt-6 border-t">
                    <button
                      onClick={handleIniciarLancamento}
                      className="w-full px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all font-semibold shadow-lg flex items-center justify-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Adicionar Novos Lançamentos
                    </button>
                  </div>
                </div>
              )}

              {/* Botão inicial quando não há resultados */}
              {mesCompetencia && (modoLancamento === 'mensal' ? resultadosAgrupados.length === 0 : resultadosFiltrados.length === 0) && (
                <div className="p-6 border-t">
                  <div className="text-center py-8">
                    <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-gray-500 mb-6">Nenhum resultado lançado para este mês</p>
                    <button
                      onClick={handleIniciarLancamento}
                      className="px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all font-semibold shadow-lg flex items-center gap-3 mx-auto"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Iniciar Lançamento de Resultados
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Formulário de Lançamento */}
          {selectedCliente && showLancamentoForm && (
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
              <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-6 py-5">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Lançamento de Resultados
                    </h3>
                    <div className="mt-3 inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg border-2 border-white/30">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="text-white font-bold text-base">
                        {new Date(mesCompetencia + '-01T00:00:00').toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={handleSalvarResultados}
                      disabled={saving || calculando || resultados.filter(r => r.valor_realizado > 0).length === 0}
                      className="px-6 py-3 bg-white text-green-600 rounded-xl font-semibold hover:bg-green-50 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                      </svg>
                      {saving ? 'Salvando...' : 'Salvar Resultados'}
                    </button>
                    
                    <button
                      onClick={handleSalvarECalcular}
                      disabled={saving || calculando || resultados.filter(r => r.valor_realizado > 0).length === 0}
                      className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-semibold hover:from-blue-600 hover:to-blue-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                      </svg>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      {saving ? 'Salvando...' : calculando ? 'Calculando...' : 'Salvar e Calcular'}
                    </button>
                  </div>
                </div>
              </div>
                      
                      <div className="p-6">
                        <div className="space-y-4">
                          {indicadoresParaMostrar.map((indicadorId: string) => {
                            return (
                              <div key={indicadorId} className="bg-white rounded-xl p-6 shadow-md border-2 border-gray-100 hover:border-green-300 transition-all">
                                {/* Nome do Indicador */}
                                <div className="mb-4">
                                  <div className="mb-3">
                                    <label className="block text-sm font-semibold text-gray-500 mb-1">Indicador</label>
                                    <div className="font-bold text-lg text-gray-800">{getIndicadorNome(indicadorId)}</div>
                                    <div className="text-xs text-gray-500 mt-1 px-2 py-1 bg-gray-100 rounded inline-block">{getIndicadorTipo(indicadorId)}</div>
                                  </div>

                                  {/* Todos os campos em uma linha */}
                                  <div className="grid grid-cols-4 gap-3">
                                    <div>
                                      <label className="block text-sm font-semibold text-gray-700 mb-2">Valor Realizado *</label>
                                      <input
                                        type={getIndicadorTipo(indicadorId) === 'monetario' ? 'text' : 'number'}
                                        step={getIndicadorTipo(indicadorId) === 'quantidade' ? '1' : '0.01'}
                                        value={getIndicadorTipo(indicadorId) === 'monetario' ? getValorFormatado(indicadorId) : getResultadoValor(indicadorId)}
                                        onChange={(e) => handleValorChange(indicadorId, e.target.value)}
                                        className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:outline-none font-semibold"
                                        placeholder={getIndicadorTipo(indicadorId) === 'monetario' ? 'R$ 0,00' : '0'}
                                      />
                                    </div>

                                    <div>
                                      <label className="block text-sm font-semibold text-gray-700 mb-2">Data Início *</label>
                                      <input
                                        type="date"
                                        value={getResultadoDataInicio(indicadorId)}
                                        onChange={(e) => handleUpdateResultado(indicadorId, 'data_inicio', e.target.value)}
                                        className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:outline-none"
                                        required
                                      />
                                    </div>
                                    
                                    <div>
                                      <label className="block text-sm font-semibold text-gray-700 mb-2">Data Fim *</label>
                                      <input
                                        type="date"
                                        value={getResultadoDataFim(indicadorId)}
                                        onChange={(e) => handleUpdateResultado(indicadorId, 'data_fim', e.target.value)}
                                        className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:outline-none"
                                        required
                                      />
                                    </div>

                                    <div>
                                      <label className="block text-sm font-semibold text-gray-700 mb-2">Observação</label>
                                      <input
                                        type="text"
                                        value={getResultadoObs(indicadorId)}
                                        onChange={(e) => handleUpdateResultado(indicadorId, 'observacao', e.target.value)}
                                        className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-green-500 focus:outline-none"
                                        placeholder="Opcional"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    </div>
                  )}
        </div>

        {/* Modal de Edição */}
        {showEditModal && resultadoEmEdicao && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 rounded-t-2xl">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Editar Resultado
                </h2>
                <p className="text-blue-100 text-sm mt-1">
                  {getIndicadorNome(resultadoEmEdicao.indicador_tipo_id)}
                </p>
              </div>

              {/* Body */}
              <div className="p-6">
                <div className="space-y-4">
                  {/* Valor Realizado */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Valor Realizado *
                    </label>
                    <input
                      type={resultadoEmEdicao && getIndicadorTipo(resultadoEmEdicao.indicador_tipo_id) === 'monetario' ? 'text' : 'number'}
                      step={resultadoEmEdicao && getIndicadorTipo(resultadoEmEdicao.indicador_tipo_id) === 'monetario' ? undefined : '0.01'}
                      value={resultadoEmEdicao && getIndicadorTipo(resultadoEmEdicao.indicador_tipo_id) === 'monetario' ? valorEdicaoFormatado : valorEdicao}
                      onChange={(e) => {
                        if (resultadoEmEdicao && getIndicadorTipo(resultadoEmEdicao.indicador_tipo_id) === 'monetario') {
                          const apenasNumeros = e.target.value.replace(/\D/g, '')
                          const valorEmCentavos = parseInt(apenasNumeros) || 0
                          const valorEmReais = valorEmCentavos / 100
                          setValorEdicao(valorEmReais.toString())
                          setValorEdicaoFormatado(formatarValorMonetario(valorEmReais))
                        } else {
                          setValorEdicao(e.target.value)
                        }
                      }}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={resultadoEmEdicao && getIndicadorTipo(resultadoEmEdicao.indicador_tipo_id) === 'monetario' ? 'R$ 0,00' : 'Digite o valor'}
                    />
                  </div>

                  {/* Período */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Data Início *
                      </label>
                      <input
                        type="date"
                        value={dataInicioEdicao}
                        onChange={(e) => setDataInicioEdicao(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Data Fim *
                      </label>
                      <input
                        type="date"
                        value={dataFimEdicao}
                        onChange={(e) => setDataFimEdicao(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Observação */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Observação (opcional)
                    </label>
                    <textarea
                      value={observacaoEdicao}
                      onChange={(e) => setObservacaoEdicao(e.target.value)}
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      placeholder="Adicione observações sobre este resultado"
                    ></textarea>
                  </div>
                </div>

                {/* Botões */}
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={handleCancelarEdicao}
                    disabled={saving}
                    className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSalvarEdicao}
                    disabled={saving}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all font-semibold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {saving ? (
                      <>
                        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Salvando...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Salvar Alterações
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </Layout>
    </ProtectedRoute>
  )
}
